import type { Store } from "@hobbyshopify/backend-client";
import { ChangeNotifier } from "subtree.js/core";
import type { LoadStatus } from "../primitives/load-status";

/** A shop and the counter the app is working at; `locationId` is null for a shop with no location yet. */
export interface Workspace {
  storeId: string;
  locationId: string | null;
}

/**
 * The merchant's shops and the shop × location the app is looking at.
 *
 * `?storeId=` in the URL (`requestStore`) wins when it names a shop the account
 * has, and an explicit `select` (after a create or a claim) wins until the URL
 * says otherwise. Every shop-scoped page reads `selectedStore` from here, and
 * every location-scoped one `locationId`.
 *
 * The chosen location lives in memory only: a reload asks again, because a
 * merchant with two counters must not silently land on yesterday's.
 */
export class StoreRepository extends ChangeNotifier {
  #status: LoadStatus = { kind: "idle" };
  #stores: readonly Store[] = [];
  #requestedId: string | null = null;
  #selectedId: string | null = null;
  #workspace: Workspace | null = null;
  #stale = false;

  get status(): LoadStatus {
    return this.#status;
  }

  get stores(): readonly Store[] {
    return this.#stores;
  }

  get isStale(): boolean {
    return this.#stale;
  }

  get selectedStore(): Store | null {
    const requested = this.#requestedId ? this.#stores.find((s) => s.id === this.#requestedId) : undefined;
    const selected = this.#selectedId ? this.#stores.find((s) => s.id === this.#selectedId) : undefined;
    return requested ?? selected ?? this.#stores[0] ?? null;
  }

  /** The id every shop-scoped call takes, or null until the shops are known (or when there are none). */
  get shopId(): string | null {
    return this.selectedStore?.id ?? null;
  }

  /** The chosen workspace, when it belongs to the selected shop. */
  get workspace(): Workspace | null {
    return this.#workspace && this.#workspace.storeId === this.shopId ? this.#workspace : null;
  }

  /** The counter every location-scoped call takes; null until one is chosen. */
  get locationId(): string | null {
    return this.workspace?.locationId ?? null;
  }

  find(id: string): Store | undefined {
    return this.#stores.find((s) => s.id === id);
  }

  setLoading(): void {
    this.#stale = false;
    this.#set({ kind: "loading" });
  }

  setStores(stores: readonly Store[]): void {
    this.#stores = [...stores];
    this.#set({ kind: "loaded" });
  }

  setError(message: string): void {
    this.#set({ kind: "error", message });
  }

  /** A shop the account just created, claimed or edited: put in place without a refetch. */
  upsert(store: Store): void {
    this.#stores = this.#stores.some((s) => s.id === store.id)
      ? this.#stores.map((s) => (s.id === store.id ? store : s))
      : [store, ...this.#stores];
    this.notifyListeners();
  }

  /** What `?storeId=` asks for; null when the URL names none. */
  requestStore(id: string | null): void {
    if (this.#requestedId === id) return;
    this.#requestedId = id;
    this.notifyListeners();
  }

  select(id: string): void {
    if (this.#selectedId === id) return;
    this.#selectedId = id;
    this.notifyListeners();
  }

  /** The picker's answer: this shop, at this counter. Clears a URL request that named another shop. */
  choose(workspace: Workspace): void {
    this.#requestedId = null;
    this.#selectedId = workspace.storeId;
    this.#workspace = { ...workspace };
    this.notifyListeners();
  }

  /** Back to the picker. */
  forgetWorkspace(): void {
    if (!this.#workspace) return;
    this.#workspace = null;
    this.notifyListeners();
  }

  /** Something changed the shops elsewhere: whoever shows them should fetch again. */
  markStale(): void {
    if (this.#stale) return;
    this.#stale = true;
    this.notifyListeners();
  }

  /** On sign-out. */
  reset(): void {
    this.#stores = [];
    this.#selectedId = null;
    this.#workspace = null;
    this.#stale = false;
    this.#set({ kind: "idle" });
  }

  #set(status: LoadStatus): void {
    this.#status = status;
    this.notifyListeners();
  }
}
